import { Report } from "@/services/backend/htz/report/types/report";
import { CardSkeleton } from "@/shared/components/card-skeleton";
import { LaboratoriesCard } from "@/routes/processes/[processId]/htz/reports/[reportId]/data/_components/laboratories-card";
import { AhuDataBaseDataCard } from "@/services/backend/htz/ahu/components/ahu-data-base-data-card";
import { ReportBaseDataCard } from "@/services/backend/htz/ahu/components/report-base-data-card";
import { AddressCard } from "@/services/backend/addresses/components/address-card";
import t from "@/lang/lang";
import {
  updateHtzReportCache,
  PatchRequest,
  useHtzReportPatchMutation,
  useHtzReportShowQuery,
} from "@/services/backend/htz/report/api/report-api";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  fieldErrorsForPrefix,
  useErrDocumentNotReady,
} from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { rtkErrIsValidationError } from "@/shared/lib/errors/validation-error";

export function ReportData({ report }: { report: Report }) {
  const { data } = useHtzReportShowQuery({ id: report.id });
  const request: PatchRequest = { id: report.id };
  // Sharing set across the components could lead
  // to a loss of input if a user extremely quickly
  // jumps from one component to the other and starts
  // input. This is just unlikely.
  const [set, { error }] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchMutation(),
  );
  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();
  const validationError = rtkErrIsValidationError(error)
    ? {
        documentId: report.id,
        documentKind: report.documentKind,
        ...error.data,
      }
    : undefined;

  // just to avoid type errors
  if (!data) return;

  return (
    <div className="space-y-2">
      <ReportBaseDataCard id={report.id} disabled={report.immutable} />
      {report.ahuDataId && (
        <AhuDataBaseDataCard
          id={report.ahuDataId}
          hideLabel
          disabled={report.immutable}
        />
      )}
      <div className="grid grid-cols-2 gap-2">
        <LaboratoriesCard report={report} />
        <AddressCard
          title={t("Erstellt durch")}
          description={t("Adresse der Niederlassung, die den Bericht erstellt")}
          address={data.createdByCompany}
          onAddressChange={(createdByCompany) =>
            set({ ...request, createdByCompany }, { ...data, createdByCompany })
          }
          fieldErrors={fieldErrorsForPrefix(
            validationError || notReadyError,
            "createdByCompany.",
          )}
          resetFieldError={(field) =>
            resetNotReadyErrorField(`createdByCompany.${field}`)
          }
          disabled={report.immutable}
        />
        <AddressCard
          title={t("Objektadresse")}
          description={t("Adresse des Objekts der Inspektion")}
          address={data.objectAddress}
          onAddressChange={(objectAddress) =>
            set({ ...request, objectAddress }, { ...data, objectAddress })
          }
          fieldErrors={fieldErrorsForPrefix(
            validationError || notReadyError,
            "objectAddress.",
          )}
          resetFieldError={(field) =>
            resetNotReadyErrorField(`objectAddress.${field}`)
          }
          disabled={report.immutable}
        />
        <AddressCard
          title={t("Erstellt für")}
          description={t("Adresse des Empfängers des Berichts")}
          address={data.createdFor}
          onAddressChange={(createdFor) =>
            set({ ...request, createdFor }, { ...data, createdFor })
          }
          fieldErrors={fieldErrorsForPrefix(
            validationError || notReadyError,
            "createdFor.",
          )}
          resetFieldError={(field) =>
            resetNotReadyErrorField(`createdFor.${field}`)
          }
          disabled={report.immutable}
        />
      </div>
    </div>
  );
}

export function ReportDataSkeleton() {
  return <CardSkeleton />;
}
